import * as React from 'react';

import { cn } from '@/lib/utils';
import type { MonthlySpend } from '@/lib/spend-api';

/**
 * Spend per month, split by what it went on.
 *
 * A STACKED BAR, not two lines and not a dual axis. Both series are money in the same
 * unit, and the question is "what did we spend in March, and on what" - so the stack
 * total is meaningful and the split answers the second half. Repair *count* is
 * deliberately NOT plotted here: it is a different unit, and a second y-scale is the
 * single most common way a chart like this starts lying. It rides in the tooltip and
 * in its own stat tile instead.
 *
 * Drawn as inline SVG rather than pulling in a charting library. This is the only
 * chart in the app; recharts is ~400 KB for twelve bars, and hand-drawing them is
 * also the only way to hit the 2px stack gap and rounded top exactly.
 *
 * The two hues are `--chart-1` and `--chart-2`, in that fixed order, and both light
 * and dark steps were run through the palette validator before use:
 *   light #008A5C / #A347D1 - worst adjacent deutan ΔE 18.3, normal ΔE 32.6
 *   dark  #10A874 / #9B5CC0 - worst adjacent deutan ΔE 16.3, normal ΔE 29.4
 * Both pass the lightness band, chroma floor, CVD separation and 3:1 contrast checks.
 */

const SERIES = [
  { key: 'assetSpend', label: 'Asset purchases', varName: '--chart-1' },
  { key: 'repairSpend', label: 'Repairs', varName: '--chart-2' },
] as const;

/**
 * INR: CargoFL is an Indian company and every date in this system already resolves in
 * Asia/Kolkata. `en-IN` also groups digits the way the finance team reads them
 * (1,20,000 rather than 120,000), which is the whole reason not to hand-roll this.
 */
const money = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

/** Compact for axis ticks, where the full figure would collide with its neighbour. */
const moneyCompact = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function formatMoney(value: string | number): string {
  return money.format(typeof value === 'string' ? Number(value) : value);
}

function monthLabel(month: string): string {
  const [year, m] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit' }).format(
    new Date(Date.UTC(year!, m! - 1, 1)),
  );
}

export function SpendChart({ months }: { months: MonthlySpend[] }) {
  const [hovered, setHovered] = React.useState<number | null>(null);
  const [showTable, setShowTable] = React.useState(false);

  const rows = months.map((m) => ({
    ...m,
    asset: Number(m.assetSpend),
    repair: Number(m.repairSpend),
    total: Number(m.assetSpend) + Number(m.repairSpend),
  }));

  const peak = Math.max(...rows.map((r) => r.total), 0);

  if (peak === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        Nothing recorded in the last 12 months. Spend appears here once assets carry a
        purchase price or repairs carry a cost.
      </p>
    );
  }

  // A "nice" ceiling so the gridline labels are round numbers rather than the peak.
  const ceiling = niceCeiling(peak);
  const GRID = 4;

  return (
    <div className="space-y-3">
      {/* Legend: always present at two series, and each is also directly labelled in
          the tooltip, so identity never rests on colour alone. */}
      <div className="flex flex-wrap items-center gap-4">
        {SERIES.map((series) => (
          <span key={series.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              className="size-2.5 rounded-[3px]"
              style={{ background: `hsl(var(${series.varName}))` }}
            />
            {series.label}
          </span>
        ))}
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="ml-auto text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          {showTable ? 'Show chart' : 'Show as table'}
        </button>
      </div>

      {showTable ? (
        <SpendTable rows={rows} />
      ) : (
        <div className="relative">
          <div className="flex gap-2">
            {/* Y axis: recessive, and compact so 1,20,000 does not wrap. */}
            <div className="flex w-[52px] shrink-0 flex-col justify-between py-[2px] text-right text-[10px] tabular-nums text-muted-foreground">
              {Array.from({ length: GRID + 1 }, (_, i) => (
                <span key={i}>{moneyCompact.format((ceiling / GRID) * (GRID - i))}</span>
              ))}
            </div>

            <div className="relative min-w-0 flex-1">
              {/* Gridlines sit behind the bars and never on top of them. */}
              <div className="absolute inset-0 flex flex-col justify-between">
                {Array.from({ length: GRID + 1 }, (_, i) => (
                  <span key={i} className="h-px w-full bg-border/60" />
                ))}
              </div>

              <div className="relative flex h-[180px] items-end gap-[3px]">
                {rows.map((row, index) => (
                  <MonthBar
                    key={row.month}
                    row={row}
                    ceiling={ceiling}
                    active={hovered === index}
                    onEnter={() => setHovered(index)}
                    onLeave={() => setHovered(null)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* X axis. Every other label below ~8 months of width would collide, so the
              months are abbreviated rather than thinned - twelve is few enough. */}
          <div className="mt-1.5 flex gap-[3px] pl-[60px]">
            {rows.map((row, index) => (
              <span
                key={row.month}
                className={cn(
                  'min-w-0 flex-1 truncate text-center text-[10px]',
                  hovered === index ? 'font-semibold text-foreground' : 'text-muted-foreground',
                )}
              >
                {monthLabel(row.month)}
              </span>
            ))}
          </div>

          {hovered !== null && rows[hovered] && (
            <Tooltip row={rows[hovered]!} index={hovered} count={rows.length} />
          )}
        </div>
      )}
    </div>
  );
}

function MonthBar({
  row,
  ceiling,
  active,
  onEnter,
  onLeave,
}: {
  row: { asset: number; repair: number; total: number };
  ceiling: number;
  active: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const pct = (value: number) => (value / ceiling) * 100;

  return (
    <div
      // The hit target is the whole column, floor to ceiling - a 6px-tall bar in a
      // quiet month would otherwise be almost impossible to hover.
      //
      // The segments are DIRECT children on purpose. Their heights are percentages, and
      // a percentage height only resolves against a parent whose own height is
      // definite; wrapping them in an auto-height div made every bar collapse to zero
      // while the axis, the table and the tooltip all still showed the real figures.
      className={cn(
        'group relative flex h-full min-w-0 flex-1 cursor-default flex-col justify-end transition-opacity',
        !active && 'opacity-90',
      )}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      tabIndex={0}
    >
      {/* Repairs on top, purchases at the base: the purchase is what happened first,
          and anchoring it to the baseline keeps the series people compare
          month-to-month on a common floor. */}
      {row.repair > 0 && (
        <div
          className="w-full shrink-0 rounded-t-[4px]"
          style={{
            height: `${pct(row.repair)}%`,
            background: 'hsl(var(--chart-2))',
            // 2px of surface between the segments, so the boundary reads without a
            // stroke - and the stack does not look like one solid block.
            marginBottom: row.asset > 0 ? 2 : 0,
          }}
        />
      )}
      {row.asset > 0 && (
        <div
          className={cn('w-full shrink-0', row.repair > 0 ? 'rounded-b-[2px]' : 'rounded-t-[4px]')}
          style={{ height: `${pct(row.asset)}%`, background: 'hsl(var(--chart-1))' }}
        />
      )}
      {active && <span className="absolute inset-0 rounded-sm bg-foreground/[0.04]" />}
    </div>
  );
}

const TOOLTIP_WIDTH = 200;

/**
 * Sits over the month it describes, not in a fixed corner.
 *
 * Pinned to `right-0` it read as a label on December whichever bar you were pointing at
 * - the value said Nov 25 while the box hung over Aug 26, which is exactly the kind of
 * mislabelling a tooltip exists to prevent.
 *
 * The offset is CSS, not a measured DOM position: the columns are equal-width flex
 * children, so the nth centre is arithmetic, and `clamp` keeps the box inside the card
 * at the first and last month where a centred one would hang off the edge. A fixed
 * width is what makes that clamp exact.
 */
function Tooltip({
  row,
  index,
  count,
}: {
  row: { month: string; asset: number; repair: number; total: number; repairCount: number; assetCount: number };
  index: number;
  count: number;
}) {
  // Measured from the left edge of the CARD, not the plot: the 52px y-axis and the 8px
  // gap beside it sit outside the bars.
  const centre = `calc(60px + (100% - 60px) * ${(index + 0.5) / count})`;

  return (
    <div
      className="pointer-events-none absolute top-0 z-10 rounded-md border bg-popover p-2.5 text-xs shadow-md"
      style={{
        width: TOOLTIP_WIDTH,
        left: `clamp(0px, calc(${centre} - ${TOOLTIP_WIDTH / 2}px), calc(100% - ${TOOLTIP_WIDTH}px))`,
      }}
    >
      <div className="mb-1.5 font-semibold">{monthLabel(row.month)}</div>
      <dl className="space-y-1">
        <Line varName="--chart-1" label="Asset purchases" value={row.asset} note={`${row.assetCount} asset${row.assetCount === 1 ? '' : 's'}`} />
        <Line varName="--chart-2" label="Repairs" value={row.repair} note={`${row.repairCount} repair${row.repairCount === 1 ? '' : 's'}`} />
        <div className="mt-1.5 flex items-baseline justify-between gap-3 border-t pt-1.5">
          <dt className="text-muted-foreground">Total</dt>
          <dd className="at-num font-semibold">{money.format(row.total)}</dd>
        </div>
      </dl>
    </div>
  );
}

function Line({
  varName,
  label,
  value,
  note,
}: {
  varName: string;
  label: string;
  value: number;
  note: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="flex items-center gap-1.5 text-muted-foreground">
        <span className="size-2 rounded-[2px]" style={{ background: `hsl(var(${varName}))` }} />
        {label}
      </dt>
      <dd className="at-num text-right">
        {money.format(value)}
        <span className="ml-1 text-[10px] text-muted-foreground">{note}</span>
      </dd>
    </div>
  );
}

/** The table view, so the numbers are reachable without reading a colour. */
function SpendTable({
  rows,
}: {
  rows: Array<{
    month: string;
    asset: number;
    repair: number;
    total: number;
    assetCount: number;
    repairCount: number;
  }>;
}) {
  return (
    <div className="max-h-[220px] overflow-auto rounded-md border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-muted/70">
          <tr className="text-left">
            <th className="px-2.5 py-1.5 font-medium">Month</th>
            <th className="px-2.5 py-1.5 text-right font-medium">Purchases</th>
            <th className="px-2.5 py-1.5 text-right font-medium">Repairs</th>
            <th className="px-2.5 py-1.5 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.month} className="border-t">
              <td className="px-2.5 py-1.5">{monthLabel(row.month)}</td>
              <td className="at-num px-2.5 py-1.5 text-right">
                {money.format(row.asset)}
                <span className="ml-1 text-[10px] text-muted-foreground">×{row.assetCount}</span>
              </td>
              <td className="at-num px-2.5 py-1.5 text-right">
                {money.format(row.repair)}
                <span className="ml-1 text-[10px] text-muted-foreground">×{row.repairCount}</span>
              </td>
              <td className="at-num px-2.5 py-1.5 text-right font-medium">
                {money.format(row.total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Round the axis top up to 1/2/5 × a power of ten, so tick labels are readable. */
function niceCeiling(peak: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  const normalized = peak / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}
